import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { UserPlus, Users, Trash2, Edit, X, Check, Shield, ChevronDown, ChevronUp, Loader } from 'lucide-react';
import { getToken } from '../../hooks/AuthProvider';

const API = process.env.REACT_APP_API_URL ?? '';

const ALL_PERMISSIONS: { key: string; label: string; description: string }[] = [
    { key: 'create_post',     label: 'Create Posts',      description: 'Create posts on your behalf' },
    { key: 'edit_post',       label: 'Edit Posts',        description: 'Edit your existing posts' },
    { key: 'delete_post',     label: 'Delete Posts',      description: 'Delete or hide your posts' },
    { key: 'manage_comments', label: 'Manage Comments',   description: 'Moderate comments on your content' },
    { key: 'view_analytics',  label: 'View Analytics',    description: 'View your analytics dashboard' },
    { key: 'manage_stream',   label: 'Manage Stream',     description: 'Manage livestream settings' },
    { key: 'manage_messages', label: 'Manage Messages',   description: 'Handle DMs on your behalf' },
];

function permissionLabel(key: string): string {
    return ALL_PERMISSIONS.find(p => p.key === key)?.label ?? key;
}

function PermissionBadge({ perm }: { perm: string }) {
    return (
        <span className="inline-block bg-gray-600 text-zinc-200 rounded-full px-2 py-0.5 text-xs mr-1 mb-1">
            {permissionLabel(perm)}
        </span>
    );
}

function PermissionCheckboxes({
    selected,
    onChange,
}: {
    selected: string[];
    onChange: (perms: string[]) => void;
}) {
    const toggle = (key: string) => {
        onChange(selected.includes(key) ? selected.filter(p => p !== key) : [...selected, key]);
    };
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
            {ALL_PERMISSIONS.map(p => (
                <label key={p.key} className="flex items-start gap-2 cursor-pointer select-none">
                    <input
                        type="checkbox"
                        className="mt-0.5 accent-red-500"
                        checked={selected.includes(p.key)}
                        onChange={() => toggle(p.key)}
                    />
                    <span>
                        <span className="text-sm text-white">{p.label}</span>
                        <span className="block text-xs text-zinc-400">{p.description}</span>
                    </span>
                </label>
            ))}
        </div>
    );
}

// ---------------------------------------------------------------------------
// INVITE FORM
// ---------------------------------------------------------------------------
function InviteForm({ onSuccess }: { onSuccess: () => void }) {
    const [open, setOpen] = useState(false);
    const [emailOrUsername, setEmailOrUsername] = useState('');
    const [permissions, setPermissions] = useState<string[]>([]);
    const [label, setLabel] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const isEmail = emailOrUsername.includes('@');

    const handleSubmit = async () => {
        if (!emailOrUsername.trim()) return toast.error('Enter an email or username.');
        if (permissions.length === 0) return toast.error('Select at least one permission.');
        setSubmitting(true);
        try {
            await axios.post(
                `${API}api/v1/team/invite`,
                {
                    [isEmail ? 'email' : 'username']: emailOrUsername.trim().toLowerCase(),
                    permissions,
                    label: label.trim(),
                },
                { headers: { 'x-auth-token': getToken() } }
            );
            toast.success('Invitation sent!');
            setEmailOrUsername('');
            setPermissions([]);
            setLabel('');
            setOpen(false);
            onSuccess();
        } catch (err: any) {
            toast.error(err?.response?.data?.error ?? 'Failed to send invitation.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="mb-6">
            {!open ? (
                <button
                    onClick={() => setOpen(true)}
                    className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                >
                    <UserPlus className="h-4 w-4" />
                    Invite Team Member
                </button>
            ) : (
                <div className="bg-gray-700 rounded-lg p-4 space-y-4">
                    <div className="flex items-center justify-between">
                        <h3 className="text-white font-semibold">Invite Team Member</h3>
                        <button onClick={() => setOpen(false)} className="text-zinc-400 hover:text-white">
                            <X className="h-4 w-4" />
                        </button>
                    </div>

                    <div>
                        <label className="block text-xs text-zinc-400 mb-1">Email or Username</label>
                        <input
                            type="text"
                            value={emailOrUsername}
                            onChange={e => setEmailOrUsername(e.target.value)}
                            placeholder="user@example.com or @username"
                            className="w-full bg-gray-900 text-white text-sm px-3 py-2 rounded-md outline-none focus:ring-1 focus:ring-red-500"
                        />
                    </div>

                    <div>
                        <label className="block text-xs text-zinc-400 mb-1">Role Label (optional)</label>
                        <input
                            type="text"
                            value={label}
                            onChange={e => setLabel(e.target.value)}
                            placeholder="e.g. Manager, Assistant"
                            maxLength={50}
                            className="w-full bg-gray-900 text-white text-sm px-3 py-2 rounded-md outline-none focus:ring-1 focus:ring-red-500"
                        />
                    </div>

                    <div>
                        <label className="block text-xs text-zinc-400 mb-1">Permissions</label>
                        <PermissionCheckboxes selected={permissions} onChange={setPermissions} />
                    </div>

                    <button
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="w-full bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white py-2 rounded-md text-sm font-medium transition-colors"
                    >
                        {submitting ? 'Sending…' : 'Send Invitation'}
                    </button>
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// EDIT MEMBER PERMISSIONS (inline panel)
// ---------------------------------------------------------------------------
function EditMemberPanel({
    member,
    onDone,
}: {
    member: any;
    onDone: () => void;
}) {
    const [permissions, setPermissions] = useState<string[]>(member.permissions ?? []);
    const [label, setLabel] = useState(member.label ?? '');
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        if (permissions.length === 0) return toast.error('Select at least one permission.');
        setSaving(true);
        try {
            await axios.put(
                `${API}api/v1/team/members/${member._id}`,
                { permissions, label: label.trim() },
                { headers: { 'x-auth-token': getToken() } }
            );
            toast.success('Member updated.');
            onDone();
        } catch (err: any) {
            toast.error(err?.response?.data?.error ?? 'Failed to update member.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="mt-3 bg-gray-900 rounded-md p-3 space-y-3">
            <div>
                <label className="block text-xs text-zinc-400 mb-1">Role Label</label>
                <input
                    type="text"
                    value={label}
                    onChange={e => setLabel(e.target.value)}
                    maxLength={50}
                    className="w-full bg-gray-800 text-white text-sm px-3 py-1.5 rounded-md outline-none focus:ring-1 focus:ring-red-500"
                />
            </div>
            <div>
                <label className="block text-xs text-zinc-400 mb-1">Permissions</label>
                <PermissionCheckboxes selected={permissions} onChange={setPermissions} />
            </div>
            <div className="flex gap-2">
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
                >
                    <Check className="h-3 w-3" /> {saving ? 'Saving…' : 'Save'}
                </button>
                <button
                    onClick={onDone}
                    className="flex items-center gap-1 bg-gray-700 hover:bg-gray-600 text-white px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
                >
                    <X className="h-3 w-3" /> Cancel
                </button>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// CREATOR SECTION
// ---------------------------------------------------------------------------
function CreatorTeamSection() {
    const [members, setMembers] = useState<any[]>([]);
    const [invitations, setInvitations] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState<string | null>(null);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [membersRes, invitesRes] = await Promise.all([
                axios.get(`${API}api/v1/team/members`, { headers: { 'x-auth-token': getToken() } }),
                axios.get(`${API}api/v1/team/invitations`, { headers: { 'x-auth-token': getToken() } }),
            ]);
            setMembers(membersRes.data.members ?? []);
            setInvitations(invitesRes.data.invitations ?? []);
        } catch {
            toast.error('Failed to load team data.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    const cancelInvite = async (id: string) => {
        try {
            await axios.post(
                `${API}api/v1/team/invite/${id}/cancel`,
                {},
                { headers: { 'x-auth-token': getToken() } }
            );
            toast.success('Invitation cancelled.');
            setInvitations(prev => prev.filter(i => i._id !== id));
        } catch (err: any) {
            toast.error(err?.response?.data?.error ?? 'Failed to cancel invitation.');
        }
    };

    const revokeMember = async (id: string) => {
        if (!window.confirm('Revoke this member\'s access?')) return;
        try {
            await axios.delete(
                `${API}api/v1/team/members/${id}`,
                { headers: { 'x-auth-token': getToken() } }
            );
            toast.success('Access revoked.');
            setMembers(prev => prev.filter(m => m._id !== id));
        } catch (err: any) {
            toast.error(err?.response?.data?.error ?? 'Failed to revoke access.');
        }
    };

    return (
        <div className="space-y-6">
            <InviteForm onSuccess={fetchData} />

            {loading ? (
                <div className="flex justify-center py-8">
                    <Loader className="h-6 w-6 animate-spin text-zinc-400" />
                </div>
            ) : (
                <>
                    {/* Pending Invitations */}
                    {invitations.length > 0 && (
                        <div>
                            <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wide mb-3">
                                Pending Invitations
                            </h3>
                            <div className="space-y-2">
                                {invitations.map(inv => (
                                    <div key={inv._id} className="flex items-center justify-between bg-gray-700 rounded-md px-4 py-3">
                                        <div>
                                            <p className="text-white text-sm">{inv.email}</p>
                                            {inv.label && <p className="text-zinc-400 text-xs mt-0.5">{inv.label}</p>}
                                            <div className="flex flex-wrap mt-1">
                                                {(inv.permissions ?? []).map((p: string) => (
                                                    <PermissionBadge key={p} perm={p} />
                                                ))}
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => cancelInvite(inv._id)}
                                            className="text-zinc-400 hover:text-red-400 transition-colors ml-4 shrink-0"
                                            title="Cancel invitation"
                                        >
                                            <X className="h-4 w-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Active Members */}
                    <div>
                        <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wide mb-3">
                            Team Members {members.length > 0 && `(${members.length})`}
                        </h3>
                        {members.length === 0 ? (
                            <div className="bg-gray-700 rounded-md px-4 py-6 text-center text-zinc-400 text-sm">
                                No team members yet. Invite someone above.
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {members.map(m => (
                                    <div key={m._id} className="bg-gray-700 rounded-md px-4 py-3">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                {m.user?.profilePic ? (
                                                    <img
                                                        src={m.user.profilePic}
                                                        alt=""
                                                        className="h-8 w-8 rounded-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="h-8 w-8 rounded-full bg-gray-600 flex items-center justify-center">
                                                        <Users className="h-4 w-4 text-zinc-400" />
                                                    </div>
                                                )}
                                                <div>
                                                    <p className="text-white text-sm font-medium">
                                                        {m.user?.displayName ?? m.user?.username ?? 'Unknown'}
                                                    </p>
                                                    {m.label && <p className="text-zinc-400 text-xs">{m.label}</p>}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => setEditingId(editingId === m._id ? null : m._id)}
                                                    className="text-zinc-400 hover:text-white transition-colors"
                                                    title="Edit permissions"
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={() => revokeMember(m._id)}
                                                    className="text-zinc-400 hover:text-red-400 transition-colors"
                                                    title="Revoke access"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap mt-2">
                                            {(m.permissions ?? []).map((p: string) => (
                                                <PermissionBadge key={p} perm={p} />
                                            ))}
                                        </div>
                                        {editingId === m._id && (
                                            <EditMemberPanel
                                                member={m}
                                                onDone={() => {
                                                    setEditingId(null);
                                                    fetchData();
                                                }}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// USER SECTION (memberships & pending invites addressed to me)
// ---------------------------------------------------------------------------
function UserTeamSection() {
    const [memberships, setMemberships] = useState<any[]>([]);
    const [myInvitations, setMyInvitations] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [acting, setActing] = useState<string | null>(null);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [membershipsRes, invitesRes] = await Promise.all([
                axios.get(`${API}api/v1/team/my-memberships`, { headers: { 'x-auth-token': getToken() } }),
                axios.get(`${API}api/v1/team/my-invitations`, { headers: { 'x-auth-token': getToken() } }),
            ]);
            setMemberships(membershipsRes.data.memberships ?? []);
            setMyInvitations(invitesRes.data.invitations ?? []);
        } catch {
            // silently fail — user may not be on any teams
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    const respondToInvite = async (token: string, action: 'accept' | 'decline') => {
        setActing(token);
        try {
            await axios.post(
                `${API}api/v1/team/invite/${action}`,
                { token },
                { headers: { 'x-auth-token': getToken() } }
            );
            toast.success(action === 'accept' ? 'You joined the team!' : 'Invitation declined.');
            fetchData();
        } catch (err: any) {
            toast.error(err?.response?.data?.error ?? `Failed to ${action} invitation.`);
        } finally {
            setActing(null);
        }
    };

    if (loading) return null;
    if (memberships.length === 0 && myInvitations.length === 0) return null;

    return (
        <div className="space-y-6 mt-8 pt-8 border-t border-gray-700">
            {/* Pending invitations TO me */}
            {myInvitations.length > 0 && (
                <div>
                    <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wide mb-3">
                        Pending Team Invitations
                    </h3>
                    <div className="space-y-2">
                        {myInvitations.map((inv: any) => (
                            <div key={inv._id} className="bg-gray-700 rounded-md px-4 py-3">
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <p className="text-white text-sm font-medium">
                                            {inv.creator?.displayName ?? 'A creator'} invited you
                                            {inv.label ? ` as ${inv.label}` : ''}
                                        </p>
                                        <div className="flex flex-wrap mt-1">
                                            {(inv.permissions ?? []).map((p: string) => (
                                                <PermissionBadge key={p} perm={p} />
                                            ))}
                                        </div>
                                    </div>
                                    <div className="flex gap-2 shrink-0">
                                        <button
                                            onClick={() => respondToInvite(inv.token, 'accept')}
                                            disabled={acting === inv.token}
                                            className="flex items-center gap-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
                                        >
                                            <Check className="h-3 w-3" /> Accept
                                        </button>
                                        <button
                                            onClick={() => respondToInvite(inv.token, 'decline')}
                                            disabled={acting === inv.token}
                                            className="flex items-center gap-1 bg-gray-600 hover:bg-gray-500 disabled:opacity-50 text-white px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
                                        >
                                            <X className="h-3 w-3" /> Decline
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* My current memberships */}
            {memberships.length > 0 && (
                <div>
                    <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wide mb-3">
                        My Team Memberships
                    </h3>
                    <div className="space-y-2">
                        {memberships.map((m: any) => (
                            <div key={m._id} className="bg-gray-700 rounded-md px-4 py-3 flex items-center gap-3">
                                {m.creator?.profilePic ? (
                                    <img
                                        src={m.creator.profilePic}
                                        alt=""
                                        className="h-8 w-8 rounded-full object-cover shrink-0"
                                    />
                                ) : (
                                    <div className="h-8 w-8 rounded-full bg-gray-600 flex items-center justify-center shrink-0">
                                        <Shield className="h-4 w-4 text-zinc-400" />
                                    </div>
                                )}
                                <div>
                                    <p className="text-white text-sm font-medium">
                                        {m.creator?.displayName ?? 'Unknown Creator'}
                                        {m.creator?.username && (
                                            <span className="text-zinc-400 font-normal ml-1">@{m.creator.username}</span>
                                        )}
                                        {m.label && <span className="text-zinc-400 font-normal ml-2">· {m.label}</span>}
                                    </p>
                                    <div className="flex flex-wrap mt-1">
                                        {(m.permissions ?? []).map((p: string) => (
                                            <PermissionBadge key={p} perm={p} />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// ROOT EXPORT
// ---------------------------------------------------------------------------
export default function TeamSettings() {
    const [isCreator, setIsCreator] = useState<boolean | null>(null);

    useEffect(() => {
        axios
            .get(`${API}api/v1/user`, { headers: { 'x-auth-token': getToken() } })
            .then(res => setIsCreator(res.data?.user?.verified === true))
            .catch(() => setIsCreator(false));
    }, []);

    return (
        <div className="p-4 bg-gray-800 rounded-lg shadow w-full">
            <div className="flex items-center gap-2 mb-6">
                <Users className="h-5 w-5 text-red-400" />
                <h2 className="text-lg font-semibold text-white">Team</h2>
            </div>

            {isCreator === null ? (
                <div className="flex justify-center py-8">
                    <Loader className="h-6 w-6 animate-spin text-zinc-400" />
                </div>
            ) : (
                <>
                    {isCreator && (
                        <div className="mb-2">
                            <p className="text-sm text-zinc-400 mb-4">
                                Invite users to act on your behalf. You control exactly what they can do and can revoke access at any time.
                            </p>
                            <CreatorTeamSection />
                        </div>
                    )}
                    <UserTeamSection />
                    {!isCreator && (
                        <p className="text-sm text-zinc-400 text-center py-4">
                            Team management is available for verified creators.
                            Your pending invitations and memberships will appear here.
                        </p>
                    )}
                </>
            )}
        </div>
    );
}
